import { AnyObject } from '@hb/shared'
import { FormApi, ValidationErrors } from 'final-form'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react'
import { useForm as useFinalForm } from 'react-final-form'

type FormProviderContextData<T extends AnyObject> = FormApi<T> & {
  saveWithoutValidation: (closeOnSuccess?: boolean) => Promise<ValidationErrors>
}

const FormProviderContext = createContext<FormProviderContextData<AnyObject> | null>(null)

export type FormProviderOnSave<T extends AnyObject> = (
  values: Partial<T>,
  form: FormApi<T>,
  closeOnSuccess?: boolean,
) => Promise<ValidationErrors>

interface FormProviderProps<T extends AnyObject> {
  onSave: FormProviderOnSave<T>
}

export const FormProvider = ({
  children,
  onSave,
}: PropsWithChildren<FormProviderProps<AnyObject>>) => {
  const form = useFinalForm<AnyObject>()

  const saveWithoutValidation = useCallback(
    async (closeOnSuccess?: boolean) => {
      const { values } = form.getState()
      return onSave(values ?? {}, form, closeOnSuccess)
    },
    [form, onSave],
  )
  const value = useMemo(() => ({ ...form, saveWithoutValidation }), [form, saveWithoutValidation])

  return (
    <FormProviderContext.Provider value={value as FormProviderContextData<AnyObject>}>
      {children}
    </FormProviderContext.Provider>
  )
}

export const useForm = <T extends AnyObject = AnyObject>() => {
  const ctx = useContext(FormProviderContext)
  if (!ctx) throw new Error('useFormProvider must be used within a FormProvider')
  return ctx as FormProviderContextData<T>
}
